import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Bell, PaperPlaneTilt, ArrowLeft } from "@phosphor-icons/react";

const TIMEZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo",
  "Europe/London", "Europe/Berlin", "Europe/Paris",
  "America/New_York", "America/Los_Angeles", "America/Chicago",
  "Australia/Sydney", "UTC",
];

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(20);
  const [minute, setMinute] = useState(0);
  const [tz, setTz] = useState("Asia/Kolkata");
  const [email, setEmail] = useState("");
  const [lastSent, setLastSent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      const r = await api.get("/settings/reminder");
      setEnabled(!!r.data.enabled);
      setHour(r.data.hour ?? 20);
      setMinute(r.data.minute ?? 0);
      setTz(r.data.timezone || "Asia/Kolkata");
      setEmail(r.data.email || me.data.email);
      setLastSent(r.data.last_sent_date);
    } catch {
      navigate("/", { replace: true });
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/reminder", {
        enabled, hour: Number(hour), minute: Number(minute),
        timezone: tz, email,
      });
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/reminders/test");
      toast.success(`Test email sent to ${r.data.to}`);
    } catch (e) {
      toast.error(
        e.response?.data?.detail
          ? `${e.response.data.detail} · Resend test-mode only sends to the API-key-owner's verified email.`
          : "Send failed"
      );
    } finally {
      setTesting(false);
    }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen paper-grain" data-testid="settings-page">
      <TopNav user={user} onLogout={async () => { await api.post("/auth/logout"); navigate("/"); }} />

      <main className="max-w-2xl mx-auto px-6 py-12">
        <button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/dashboard")}
          className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground hover:text-foreground flex items-center gap-2 mb-10"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Settings</p>
        <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tighter mt-3 mb-10">
          A gentle <span className="italic text-accent">nudge</span>
        </h1>

        <section className="border border-border rounded-lg bg-card p-6 space-y-6" data-testid="reminder-card">
          <div className="flex items-start justify-between gap-6">
            <div>
              <div className="flex items-center gap-2">
                <Bell size={20} weight="duotone" className="text-primary" />
                <h2 className="font-serif text-2xl">Daily reminder email</h2>
              </div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-md">
                One quiet nudge each evening — no pressure. We'll skip days you've already written.
              </p>
            </div>
            <label className="inline-flex items-center cursor-pointer" data-testid="reminder-toggle">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="peer sr-only"
              />
              <div className="relative w-11 h-6 bg-muted peer-checked:bg-primary rounded-full transition-colors">
                <div className={`absolute top-0.5 left-0.5 h-5 w-5 bg-background rounded-full transition-transform ${enabled ? "translate-x-5" : ""}`} />
              </div>
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Time</label>
              <div className="flex items-center gap-2">
                <select
                  data-testid="reminder-hour"
                  value={hour}
                  onChange={(e) => setHour(Number(e.target.value))}
                  disabled={!enabled}
                  className="bg-transparent border border-border rounded-lg px-3 py-2 text-lg font-serif disabled:opacity-50"
                >
                  {Array.from({length: 24}, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2,"0")}</option>
                  ))}
                </select>
                <span className="text-xl text-muted-foreground">:</span>
                <select
                  data-testid="reminder-minute"
                  value={minute}
                  onChange={(e) => setMinute(Number(e.target.value))}
                  disabled={!enabled}
                  className="bg-transparent border border-border rounded-lg px-3 py-2 text-lg font-serif disabled:opacity-50"
                >
                  {[0,15,30,45].map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2,"0")}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Timezone</label>
              <select
                data-testid="reminder-tz"
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                disabled={!enabled}
                className="w-full bg-transparent border border-border rounded-lg px-3 py-2 disabled:opacity-50"
              >
                {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Deliver to</label>
            <Input
              data-testid="reminder-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!enabled}
              className="bg-transparent"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Currently in Resend test mode — real emails only reach the address that owns the Resend API key.
              Verify a domain on Resend to send to any address.
            </p>
          </div>

          {lastSent && (
            <p className="text-xs text-muted-foreground" data-testid="reminder-last-sent">
              Last sent: {lastSent}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              data-testid="save-reminder-btn"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
            <Button
              data-testid="test-reminder-btn"
              onClick={sendTest}
              disabled={testing}
              variant="ghost"
              className="text-xs uppercase tracking-[0.2em]"
            >
              <PaperPlaneTilt size={14} className="mr-2" />
              {testing ? "Sending…" : "Send test email"}
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
